import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import type { Tables, TablesInsert, TablesUpdate } from '@/lib/supabase/supabase-types';

type WorkExperience = Tables<'work_experience'>;

interface WorkExperienceFormProps {
  experience?: WorkExperience;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<TablesInsert<'work_experience'>, 'freelancer_profile_id'>) => Promise<void>;
  onUpdate?: (data: { id: string } & TablesUpdate<'work_experience'>) => Promise<void>;
  isLoading?: boolean;
}

export function WorkExperienceForm({
  experience,
  isOpen,
  onClose,
  onSubmit,
  onUpdate,
  isLoading = false,
}: WorkExperienceFormProps) {
  const [jobTitle, setJobTitle] = useState('');
  const [company, setCompany] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCurrent, setIsCurrent] = useState(false);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [jobTitleError, setJobTitleError] = useState<string | undefined>();
  const [companyError, setCompanyError] = useState<string | undefined>();
  const [startDateError, setStartDateError] = useState<string | undefined>();
  const [endDateError, setEndDateError] = useState<string | undefined>();

  const isEditing = !!experience;

  /**
   * Validate job title
   * Returns error message if invalid, undefined if valid
   */
  const validateJobTitle = (title: string): string | undefined => {
    const trimmed = title.trim();
    
    if (!trimmed) {
      return 'Job title is required';
    }
    
    if (trimmed.length < 2) {
      return 'Job title must be at least 2 characters long';
    }
    
    if (trimmed.length > 200) {
      return 'Job title must be less than 200 characters';
    }
    
    return undefined;
  };

  /**
   * Validate company name
   * Returns error message if invalid, undefined if valid
   */
  const validateCompany = (company: string): string | undefined => {
    const trimmed = company.trim();
    
    if (!trimmed) {
      return 'Company name is required';
    }
    
    if (trimmed.length < 2) {
      return 'Company name must be at least 2 characters long';
    }
    
    if (trimmed.length > 200) {
      return 'Company name must be less than 200 characters';
    }
    
    return undefined;
  };

  /**
   * Validate start date
   * Returns error message if invalid, undefined if valid
   */
  const validateStartDate = (date: string): string | undefined => {
    if (!date) {
      return 'Start date is required';
    }
    
    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      return 'Start date must be in YYYY-MM-DD format';
    }
    
    // Validate that date is not in the future
    const dateObj = new Date(date);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    
    if (dateObj > today) {
      return 'Start date cannot be in the future';
    }
    
    return undefined;
  };

  /**
   * Validate end date
   * Returns error message if invalid, undefined if valid
   */
  const validateEndDate = (endDate: string, startDate: string, isCurrentJob: boolean): string | undefined => {
    // End date is optional if current job
    if (isCurrentJob || !endDate) {
      return undefined;
    }
    
    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(endDate)) {
      return 'End date must be in YYYY-MM-DD format';
    }
    
    // Validate that end date is after start date
    if (startDate && new Date(endDate) < new Date(startDate)) {
      return 'End date must be after start date';
    }
    
    // Validate that end date is not in the future
    const dateObj = new Date(endDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    
    if (dateObj > today) {
      return 'End date cannot be in the future';
    }
    
    return undefined;
  };

  // Initialize form with experience data
  useEffect(() => {
    if (experience) {
      setJobTitle(experience.job_title);
      setCompany(experience.company);
      setStartDate(experience.start_date);
      setEndDate(experience.end_date || '');
      setIsCurrent(!experience.end_date);
      setLocation(experience.location || '');
      setDescription(experience.description || '');
      // Reset errors when loading existing experience
      setJobTitleError(undefined);
      setCompanyError(undefined);
      setStartDateError(undefined);
      setEndDateError(undefined);
    } else {
      // Reset form for new experience
      setJobTitle('');
      setCompany('');
      setStartDate('');
      setEndDate('');
      setIsCurrent(false);
      setLocation('');
      setDescription('');
      setJobTitleError(undefined);
      setCompanyError(undefined);
      setStartDateError(undefined);
      setEndDateError(undefined);
    }
  }, [experience, isOpen]);

  const handleJobTitleChange = (value: string) => {
    setJobTitle(value);
    // Validate on change for real-time feedback
    const error = validateJobTitle(value);
    setJobTitleError(error);
  };

  const handleJobTitleBlur = () => {
    const error = validateJobTitle(jobTitle);
    setJobTitleError(error);
  };

  const handleCompanyChange = (value: string) => {
    setCompany(value);
    // Validate on change for real-time feedback
    const error = validateCompany(value);
    setCompanyError(error);
  };

  const handleCompanyBlur = () => {
    const error = validateCompany(company);
    setCompanyError(error);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    // Validate on change for real-time feedback
    const error = validateStartDate(value);
    setStartDateError(error);
    // Re-validate end date if it exists
    if (endDate) {
      const endDateErr = validateEndDate(endDate, value, isCurrent);
      setEndDateError(endDateErr);
    }
  };

  const handleStartDateBlur = () => {
    const error = validateStartDate(startDate);
    setStartDateError(error);
    // Re-validate end date if it exists
    if (endDate) {
      const endDateErr = validateEndDate(endDate, startDate, isCurrent);
      setEndDateError(endDateErr);
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    // Validate on change for real-time feedback
    const error = validateEndDate(value, startDate, isCurrent);
    setEndDateError(error);
  };

  const handleEndDateBlur = () => {
    const error = validateEndDate(endDate, startDate, isCurrent);
    setEndDateError(error);
  };

  const handleIsCurrentChange = (checked: boolean) => {
    setIsCurrent(checked);
    if (checked) {
      setEndDate('');
      setEndDateError(undefined);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all required fields
    const jobTitleErr = validateJobTitle(jobTitle);
    const companyErr = validateCompany(company);
    const startDateErr = validateStartDate(startDate);
    const endDateErr = validateEndDate(endDate, startDate, isCurrent);

    // Set all errors
    setJobTitleError(jobTitleErr);
    setCompanyError(companyErr);
    setStartDateError(startDateErr);
    setEndDateError(endDateErr);

    // If any validation fails, show error and return
    if (jobTitleErr || companyErr || startDateErr || endDateErr) {
      if (jobTitleErr) {
        toast.error(jobTitleErr);
      } else if (companyErr) {
        toast.error(companyErr);
      } else if (startDateErr) {
        toast.error(startDateErr);
      } else if (endDateErr) {
        toast.error(endDateErr);
      }
      return;
    }

    try {
      const experienceData = {
        job_title: jobTitle.trim(),
        company: company.trim(),
        start_date: startDate,
        end_date: isCurrent ? null : (endDate || null),
        location: location.trim() || null,
        description: description.trim() || null,
      };

      if (isEditing && onUpdate && experience) {
        await onUpdate({
          id: experience.id,
          ...experienceData,
        });
      } else {
        await onSubmit(experienceData);
      }
      onClose();
    } catch (error) {
      // Error handling is done in the hook
      console.error('Error saving work experience:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!max-w-3xl max-h-[90vh] overflow-y-auto rounded-[24px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-normal text-slate-900">
            {isEditing ? 'Edit Work Experience' : 'Add Work Experience'}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {isEditing ? 'Update your work experience details' : 'Add a new work experience entry'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job Title */}
          <div className="space-y-2">
            <Label htmlFor="jobTitle" className="text-sm font-semibold text-slate-700">Job Title *</Label>
            <Input
              id="jobTitle"
              placeholder="Senior ML Engineer"
              value={jobTitle}
              onChange={(e) => handleJobTitleChange(e.target.value)}
              onBlur={handleJobTitleBlur}
              required
              maxLength={200}
              className={`rounded-xl border-2 focus:ring-0 ${
                jobTitleError 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-slate-200 focus:border-[#4669ed]'
              }`}
            />
            {jobTitleError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {jobTitleError}
              </p>
            )}
            <p className="text-xs text-slate-500">{jobTitle.length}/200 characters</p>
          </div>

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="company" className="text-sm font-semibold text-slate-700">Company *</Label>
            <Input
              id="company"
              placeholder="Shopify"
              value={company}
              onChange={(e) => handleCompanyChange(e.target.value)}
              onBlur={handleCompanyBlur}
              required
              maxLength={200}
              className={`rounded-xl border-2 focus:ring-0 ${
                companyError 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-slate-200 focus:border-[#4669ed]'
              }`}
            />
            {companyError && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {companyError}
              </p>
            )}
            <p className="text-xs text-slate-500">{company.length}/200 characters</p>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate" className="text-sm font-semibold text-slate-700">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
                onBlur={handleStartDateBlur}
                required
                max={new Date().toISOString().split('T')[0]}
                className={`rounded-xl border-2 focus:ring-0 ${
                  startDateError 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-slate-200 focus:border-[#4669ed]'
                }`}
              />
              {startDateError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {startDateError}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate" className="text-sm font-semibold text-slate-700">
                End Date {!isCurrent && '(Optional)'}
              </Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
                onBlur={handleEndDateBlur}
                disabled={isCurrent}
                max={new Date().toISOString().split('T')[0]}
                className={`rounded-xl border-2 focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                  endDateError 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-slate-200 focus:border-[#4669ed]'
                }`}
              />
              {endDateError && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {endDateError}
                </p>
              )}
            </div>
          </div>

          {/* Current Job Checkbox */}
          <div className="flex items-center space-x-3">
            <Checkbox
              id="isCurrent"
              checked={isCurrent}
              onCheckedChange={(checked) => handleIsCurrentChange(checked === true)}
            />
            <Label htmlFor="isCurrent" className="text-sm font-normal cursor-pointer text-slate-700">
              I currently work here
            </Label>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-sm font-semibold text-slate-700">Location (Optional)</Label>
            <Input
              id="location"
              placeholder="San Francisco, CA"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={200}
              className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0"
            />
            <p className="text-xs text-slate-500">{location.length}/200 characters</p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-slate-700">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Led ML personalization team building real-time recommendation systems processing 10M+ predictions daily.&#10;Increased conversion by 62% through collaborative filtering and context-aware models."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              maxLength={5000}
              className="rounded-xl border-2 border-slate-200 focus:border-[#4669ed] focus:ring-0 resize-none"
            />
            <div className="flex justify-between">
            <p className="text-xs text-slate-500">
              Describe your responsibilities and achievements. You can use bullet points (•) or line breaks.
              </p>
              <p className="text-xs text-slate-500">{description.length}/5000 characters</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-[48px] border-2 border-slate-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !jobTitle.trim() || !company.trim() || !startDate}
              className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update Experience' : 'Add Experience'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

